import { Mail02Icon } from "Icons/Mail02";

import { useCallback, useEffect, useState } from "react";
import Turnstile from "react-turnstile";

import { css } from "@emotion/react";

import { useRouter } from "next/router";

import { MailBoxLinks } from "components/onboarding/MailBoxLinks";
import { Button } from "components/shared/library/Button";
import { Typography } from "components/shared/library/Typography";

import { useAlert } from "contexts/alertContext";

import { signInWithOtp } from "apis/authentication";

import { isViewMobile } from "utils/mediaQueryStrings";
import { getExternalPath, getOnboardingUrl } from "utils/urls";

import { purple, uiGray } from "constants/colors";
import { signUpResendWaitTime } from "constants/time";

import { ButtonLink } from "../library/ButtonLink";
import { RectangleToast } from "../library/RectangleToast";
import { OnboardingContainer } from "./OnboardingContainer";
import { TwoPanelCard } from "./TwoPanelCard";

const style = {
  content: css({
    display: "flex",
    gap: 24,
    justifyContent: "space-between",
    height: "100%",
    flexDirection: "column",
  }),
  rightPanelTop: css({
    display: "flex",
    flexDirection: "column",
    gap: 48,
  }),
  toast: css({
    display: "flex",
    flexDirection: "column",
  }),
  hidden: css({ display: "hidden" }),
  bottomRightPanel: css({
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "center",
  }),
  wrongEmail: css({
    display: "flex",
    alignItems: "center",
    gap: 4,
  }),
};

type Props = {
  email: string;
  resetEmail?: () => void;
};

export const CheckEmailScreen = ({ email, resetEmail }: Props) => {
  const [resendTimer, setResendTimer] = useState(signUpResendWaitTime / 1000);
  const [captchaToken, setCaptchaToken] = useState("");

  const router = useRouter();
  const { createAlert } = useAlert();

  const startTimer = useCallback(() => {
    const timerInterval = setInterval(() => {
      setResendTimer((prevTimer) => {
        const newTimer = prevTimer - 1;

        // Clear the interval and stop the timer when it reaches 0
        if (newTimer === 0) {
          clearInterval(timerInterval);
        }

        return newTimer;
      });
    }, 1000);
  }, [setResendTimer]);

  useEffect(() => {
    startTimer();
  }, [startTimer]);

  return (
    <OnboardingContainer>
      <TwoPanelCard
        title="Check your inbox"
        content={
          <>
            {!isViewMobile() && (
              <ButtonLink
                href="mailto:support@ribbon.ai"
                IconLeft={Mail02Icon}
                background="ghost"
                color="gray"
              ></ButtonLink>
            )}
          </>
        }
        subtitle={
          <>
            <Typography
              color={uiGray[70]}
            >{`We've sent a magic link to `}</Typography>
            <Typography weight="bold" color={purple[800]}>
              {email}
            </Typography>
            <Typography
              color={uiGray[70]}
            >{`. Please click the link to confirm your email.`}</Typography>
          </>
        }
      >
        <div css={style.content}>
          <div css={style.rightPanelTop}>
            <RectangleToast
              title="Can't find the email?"
              customCss={style.toast}
            >
              <Typography color={purple[600]} weight="medium">
                Please check your spam folder.
              </Typography>
            </RectangleToast>
            <MailBoxLinks />
            <Turnstile
              appearance="always"
              theme="light"
              sitekey={process.env.NEXT_PUBLIC_CLOUDFLARE_SITE_KEY || ""}
              onSuccess={(token) => {
                setCaptchaToken(token);
              }}
              onError={() => {
                createAlert(
                  "error",
                  "Captcha failed. Please reload the page and try again."
                );
              }}
            />
          </div>

          <div css={style.bottomRightPanel}>
            <Button
              onClick={async () => {
                // todo: doesn't look like there's a way to trigger email resend in Supabase
                // we can opt to sign in the user via OTP instead
                // feels a little weird to do this, but it's the only way to trigger an email
                const { error } = await signInWithOtp({
                  email,
                  redirectUrl: getExternalPath(
                    getOnboardingUrl({ currentPath: router.asPath })
                  ),
                  captchaToken,
                });

                if (!error) return true;

                createAlert(
                  "error",
                  typeof error === "string"
                    ? error
                    : error.message || "An error occurred"
                );
              }}
              onClickSuccess={() => {
                setResendTimer(signUpResendWaitTime / 1000);
                createAlert("success", "Email sent! Check your inbox.");
                startTimer();
              }}
              isFullWidth
              size="large"
              background="filled"
              disabled={resendTimer !== 0 || !captchaToken}
            >
              {resendTimer !== 0
                ? `Resend Email in ${resendTimer} seconds`
                : "Resend Email"}
            </Button>
            {resetEmail && (
              <div css={style.wrongEmail}>
                <Typography color={uiGray[80]}>{`Wrong email?`}</Typography>
                <Button onClick={resetEmail} background="link">
                  Re-enter your email
                </Button>
              </div>
            )}
          </div>
        </div>
      </TwoPanelCard>
    </OnboardingContainer>
  );
};
